import {
  CascaderField,
  DateRangePickerField,
  Form,
  SelectField,
  SubmitButton,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  useAccountTitleOptions,
  useAccountingType,
  useAccountingTypes,
  useExpenditureBudgetJurisdictionOptions,
  useExpenditureBudgetOrganizationOptions,
  useExpenditurePurposeOptions,
} from '../../hooks';
import { ExpenditureBudgetPurposeFormSchema as ExpenditureBudgetPurposeForMajorMatterFormSchema } from '../../models';

type FormValues = z.infer<
  typeof ExpenditureBudgetPurposeForMajorMatterFormSchema
>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const ExpenditureItemMajorMatterForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    resetField,
    watch,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      ExpenditureBudgetPurposeForMajorMatterFormSchema.refine(
        (values) =>
          accountingType.data?.type === 'special' || values.organizationId,
        {
          path: ['organizationId'],
          message: t('validation.required'),
        },
      ).refine(
        (values) =>
          accountingType.data?.type === 'general' || values.accountTitleId,
        {
          path: ['accountTitleId'],
          message: t('validation.required'),
        },
      ),
    ),
  });

  const accountingTypeOptions = useAccountingTypes({
    select: (data) =>
      data.map((d) => ({
        value: d.id,
        label: `${d.code} ${d.name}`,
      })),
  });
  const jurisdictionOptions = useExpenditureBudgetJurisdictionOptions();
  const organizationOptions = useExpenditureBudgetOrganizationOptions();
  const accountingId = watch('accountingId');
  const accountingType = useAccountingType({ id: accountingId ?? '' });
  const expenditurePurposeOptions = useExpenditurePurposeOptions({
    showCode: true,
    accountingId: accountingId ?? '',
    params: { isMajorMatter: true },
  });
  const accountTitleOptions = useAccountTitleOptions({
    accountingId: accountingId ?? '',
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="accountingId"
        label={t('accounting-name')}
        control={control}
        errors={errors}
        options={accountingTypeOptions.data}
        required
        shouldCoexistOnChange
        onChange={() => {
          resetField('accountTitleId', {
            keepDirty: false,
            keepTouched: false,
            keepError: false,
            defaultValue: null,
          });

          resetField('expenditurePurposeIds', {
            keepDirty: false,
            keepTouched: false,
            keepError: false,
            defaultValue: [],
          });
        }}
      />

      <SelectField
        name="jurisdictionId"
        label={t('jurisdiction')}
        control={control}
        errors={errors}
        options={jurisdictionOptions.data}
        required
      />

      <SelectField
        name="organizationId"
        label={t('organization')}
        control={control}
        errors={errors}
        options={organizationOptions.data}
        disabled={accountingType.data?.type === 'special'}
        required={accountingType.data?.type === 'general'}
      />

      <SelectField
        name="accountTitleId"
        label={t('account-title')}
        control={control}
        errors={errors}
        options={accountTitleOptions.data}
        disabled={accountingType.data?.type === 'general'}
        required={accountingType.data?.type === 'special'}
      />

      <CascaderField
        name="expenditurePurposeIds"
        label={t('expenditure-purpose-level-3-1')}
        control={control}
        errors={errors}
        loading={expenditurePurposeOptions.isPending}
        options={expenditurePurposeOptions.data?.filter(
          (v) => v.children?.length,
        )}
        required
      />
      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
